import {
  AppointmentStatusCodes,
  DataBlockVariant,
  useEnabledDataBlockTypes,
  VisitDto,
} from '@lmt/shared-types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { OptionProps } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/IconDropDownButton';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  caseInformationDataBlockTypeName,
  useDataBlockTypeTranslation,
  visitInformationDataBlockTypeName,
} from '@lmt/unicorn-shared/app/components/measurements/common';
import {
  getFormattedDateTime,
  getFormattedDateTimeRange,
  getFormattedLongDate,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import EmailSentIcon from '@lmt/unicorn-shared/app/icons/email-sent.svg?react';
import TrashIcon from '@lmt/unicorn-shared/app/icons/trash.svg?react';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CaseItem,
  CaseItemType,
  CaseTreeItemType,
  SubCaseItemType,
} from '../../types';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';

import styles from './CaseContents.module.scss';
import { CaseTreeItem } from './CaseTreeItem';
import { getItemPrefix, getMeasurementOptions } from './common';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useEventCallback } from '@mui/material';

interface CaseContentsProps {
  patientId: number;
  caseId: number;
  caseItems: CaseItem[];
  changeCurrentItem: (itemId: string) => void;
  removeCase: (id: number) => void;
  reopenCase: (id: number) => void;
  isReadOnly: boolean;
}

export const CaseContents: React.FC<CaseContentsProps> = React.memo(
  function CaseContents(props) {
    const { patientId, caseId, caseItems, isReadOnly, changeCurrentItem } =
      props;
    const i18next = useTranslation();
    const i18nDictionaries = useScopedTranslation(
      'data_block_types',
      NS_DataBlockFields,
    );
    const forms = useGlobalForms();
    const claims = useClaims();
    const caseContentsRef = useRef<HTMLDivElement>(null);
    const visitTypes = useVisitTypes();
    const dataBlockTypes = useEnabledDataBlockTypes();
    const translateDataBlockType = useDataBlockTypeTranslation();
    const dispatch = useUniDispatch();
    const modals = useModal();

    const caseDataBlockTypes = useMemo(() => {
      return dataBlockTypes.filter(
        (type) =>
          type.variant === DataBlockVariant.Case &&
          !type.isDeactivated &&
          type.value !== caseInformationDataBlockTypeName,
      );
    }, [dataBlockTypes]);

    const visitDataBlockTypes = useMemo(() => {
      return dataBlockTypes.filter(
        (type) =>
          type.variant === DataBlockVariant.Visit &&
          !type.isDeactivated &&
          type.value !== visitInformationDataBlockTypeName,
      );
    }, [dataBlockTypes]);

    const measurementDataBlockTypes = useMemo(() => {
      return dataBlockTypes.filter(
        (type) =>
          type.variant === DataBlockVariant.Measurement && !type.isDeactivated,
      );
    }, [dataBlockTypes]);

    const lastCaseItemPrefix = useMemo(() => {
      if (caseItems && caseItems.length > 0) {
        return getItemPrefix(caseItems[caseItems.length - 1].id);
      }
      return undefined;
    }, [caseItems]);

    const createDataBlock = useEventCallback(
      (
        dataBlockTypeId: number,
        patientId: number,
        caseId: number,
        dataBlockTypeName: string,
        isOnlyOnePerCase: boolean,
        visitId?: number,
        isOnlyOnePerVisit?: boolean,
      ) => {
        if (isOnlyOnePerCase && !visitId) {
          let isExist = false;
          caseItems.map((caseItem) => {
            switch (caseItem.type) {
              case CaseItemType.caseData:
                if (
                  caseItem.subitems.some(
                    (item) => item.item.typeId === dataBlockTypeId,
                  )
                ) {
                  isExist = true;
                  return;
                }
                break;
              case CaseItemType.measurement:
                if (caseItem.item.typeId === dataBlockTypeId) {
                  isExist = true;
                  return;
                }
                break;
            }
          });

          if (isExist) {
            return modals.showAlert({
              title: i18next.t(
                'patients.cases.confirmations.case_datablock_exists_title',
              ),
              text: i18next.t(
                'patients.cases.confirmations.case_datablock_exists_text',
              ),
            });
          }
        }
        if (isOnlyOnePerVisit && visitId) {
          const visitItem = caseItems.find(
            (caseItem) =>
              caseItem.type === CaseItemType.visit &&
              caseItem.item.id === visitId,
          );
          if (
            visitItem &&
            visitItem.subitems.some(
              (item) => item.item.typeId === dataBlockTypeId,
            )
          ) {
            return modals.showAlert({
              title: i18next.t(
                'patients.cases.confirmations.visit_datablock_exists_title',
              ),
              text: i18next.t(
                'patients.cases.confirmations.visit_datablock_exists_text',
              ),
            });
          }
        }
        forms.openDataBlockCreation(
          dataBlockTypeId,
          patientId,
          caseId,
          visitId,
          i18next.t('global_forms.create_data_block_of_type', {
            type: dataBlockTypeName,
          }),
        );
      },
    );

    const measurementOptions = useMemo(
      () =>
        measurementDataBlockTypes.map((measurementType) => {
          const measurementTypeName = translateDataBlockType(
            measurementType,
          ) as string;
          const option: OptionProps = {
            value: measurementType.id,
            displayValue: measurementTypeName,
            onClick: () =>
              createDataBlock(
                measurementType.id,
                patientId,
                caseId,
                measurementTypeName,
                measurementType.isOnlyOnePerCase,
              ),
          };
          return option;
        }),
      [measurementDataBlockTypes, createDataBlock, patientId, caseId],
    );

    const visitOption: OptionProps = useMemo(() => {
      return {
        value: 'visit',
        displayValue: i18next.t('patients.cases.new_visit_option') as string,
        onClick: () =>
          forms.openVisitCreating(undefined, undefined, patientId, caseId),
      };
    }, [forms]);

    const CaseItemsList = (
      <>
        {caseItems.map((caseItem) => {
          switch (caseItem.type) {
            case CaseItemType.caseData:
              return (
                <div key={caseItem.id}>
                  <CaseTreeItem
                    id={`${caseItem.id}_header`}
                    navId={caseItem.subitems[0]?.id}
                    isHeader
                    isSubitem={false}
                    onClick={changeCurrentItem}
                    title={i18next.t('patients.cases.case_data')}
                    type={
                      !isReadOnly
                        ? CaseTreeItemType.addContent
                        : CaseTreeItemType.empty
                    }
                    options={[visitOption]
                      .concat(
                        caseDataBlockTypes.map((caseType) => {
                          const caseTypeName = translateDataBlockType(
                            caseType,
                          ) as string;
                          const option: OptionProps = {
                            value: caseType.id,
                            displayValue: caseTypeName,
                            onClick: () =>
                              createDataBlock(
                                caseType.id,
                                patientId,
                                caseId,
                                caseTypeName,
                                caseType.isOnlyOnePerCase,
                              ),
                          };
                          return option;
                        }),
                      )
                      .concat(measurementOptions)}
                    isLastElement={caseItems.length === 0}
                    isLastBranch={caseItem.id === lastCaseItemPrefix}
                  />
                  {caseItem.subitems.map((dataBlock) => (
                    <CaseTreeItem
                      id={dataBlock.id}
                      isSubitem
                      onClick={changeCurrentItem}
                      key={`${dataBlock.id}`}
                      title={
                        translateDataBlockType(
                          dataBlockTypes.find(
                            (type) => type.id === dataBlock.item.typeId,
                          )!,
                        ) as string
                      }
                      isLastElement={
                        caseItem.subitems[caseItem.subitems.length - 1].id ===
                        dataBlock.id
                      }
                      isLastBranch={caseItem.id === lastCaseItemPrefix}
                      {...getMeasurementOptions(dataBlock.transportInfo)}
                    />
                  ))}
                </div>
              );
            case CaseItemType.visit:
              const visit = caseItem.item as VisitDto;
              return (
                <div key={caseItem.id}>
                  <CaseTreeItem
                    id={`${caseItem.id}_header`}
                    navId={caseItem.id}
                    onClick={changeCurrentItem}
                    isHeader
                    isSubitem={false}
                    title={i18next.t(
                      (visitTypes.find(
                        (visitType) => visitType.id === visit.typeId,
                      )?.value || '-') as any,
                    )}
                    type={
                      !isReadOnly
                        ? CaseTreeItemType.addContent
                        : CaseTreeItemType.empty
                    }
                    options={visitDataBlockTypes
                      .concat(measurementDataBlockTypes)
                      .map((visitType) => {
                        const visitTypeName = translateDataBlockType(
                          visitType,
                        ) as string;
                        const option: OptionProps = {
                          value: visitType.id,
                          displayValue: visitTypeName,
                          onClick: () =>
                            createDataBlock(
                              visitType.id,
                              patientId,
                              caseId,
                              visitTypeName,
                              visitType.isOnlyOnePerCase,
                              visit.id,
                              visitType.isOnlyOnePerVisit,
                            ),
                        };
                        return option;
                      })}
                    subtitle={
                      <div className={styles.visitTimestamp}>
                        <div>
                          {getFormattedDateTimeRange(visit.start, visit.end)}
                        </div>
                        {!!visit.lastPatientNotificationDateTime ? (
                          <AppTooltip
                            className={styles.notificationTooltip}
                            title={i18next.t(
                              'patients.cases.last_notification_datetime',
                              {
                                timestamp: getFormattedDateTime(
                                  visit.lastPatientNotificationDateTime ??
                                    new Date(),
                                ),
                              },
                            )}
                          >
                            <EmailSentIcon
                              className={styles.visitTimestampIcon}
                            />
                          </AppTooltip>
                        ) : null}
                      </div>
                    }
                    isLastElement={caseItems.length === 0}
                    isLastBranch={caseItem.id === lastCaseItemPrefix}
                  />
                  {caseItem.subitems.map((measurement) =>
                    measurement.type === SubCaseItemType.visitPreview ? (
                      <CaseTreeItem
                        id={caseItem.id}
                        isSubitem
                        onClick={changeCurrentItem}
                        title={i18nDictionaries.t('visit.visit information')}
                        type={CaseTreeItemType.showStatus}
                        options={
                          visit.statusId ?? AppointmentStatusCodes.Planned
                        }
                        isLastElement={
                          caseItem.subitems.filter(
                            (measurement) =>
                              measurement.type != SubCaseItemType.visitPreview,
                          ).length === 0
                        }
                        isLastBranch={caseItem.id === lastCaseItemPrefix}
                      />
                    ) : measurement.type === SubCaseItemType.visitInfo ? (
                      <CaseTreeItem
                        id={measurement.id}
                        isSubitem
                        onClick={changeCurrentItem}
                        key={`${measurement.id}`}
                        title={
                          translateDataBlockType(
                            dataBlockTypes.find(
                              (type) => type.id === measurement.item.typeId,
                            )!,
                          ) as string
                        }
                        isLastElement={
                          caseItem.subitems[caseItem.subitems.length - 1].id ===
                          measurement.id
                        }
                        isLastBranch={caseItem.id === lastCaseItemPrefix}
                        {...getMeasurementOptions(measurement.transportInfo)}
                      />
                    ) : (
                      <CaseTreeItem
                        id={measurement.id}
                        isSubitem
                        onClick={changeCurrentItem}
                        key={`${measurement.id}`}
                        title={
                          translateDataBlockType(
                            dataBlockTypes.find(
                              (type) => type.id === measurement.item.typeId,
                            )!,
                          ) as string
                        }
                        subtitle={
                          measurement.item.beginDateContainsTime
                            ? getFormattedDateTimeRange(measurement.date)
                            : getFormattedLongDate(measurement.date)
                        }
                        isLastElement={
                          caseItem.subitems[caseItem.subitems.length - 1].id ===
                          measurement.id
                        }
                        isLastBranch={caseItem.id === lastCaseItemPrefix}
                        {...getMeasurementOptions(measurement.transportInfo)}
                      />
                    ),
                  )}
                </div>
              );
            case CaseItemType.measurement:
              return (
                <CaseTreeItem
                  key={caseItem.id}
                  id={`${caseItem.id}_header`}
                  navId={caseItem.id}
                  isSubitem={false}
                  onClick={changeCurrentItem}
                  title={
                    translateDataBlockType(
                      dataBlockTypes.find(
                        (type) => type.id === caseItem.item.typeId,
                      )!,
                    ) as string
                  }
                  subtitle={
                    caseItem.item.beginDateContainsTime
                      ? getFormattedDateTimeRange(caseItem.date)
                      : getFormattedLongDate(caseItem.date)
                  }
                  isLastElement={false}
                  isLastBranch={caseItem.id === lastCaseItemPrefix}
                  {...getMeasurementOptions(caseItem.transportInfo)}
                />
              );
          }
        })}
      </>
    );

    return (
      <div
        className={styles.mainContainer}
        data-test-id={'case-table-of-contents-root'}
      >
        <div className={styles.container}>
          <div ref={caseContentsRef} className={styles.addCaseContents}>
            <div className={styles.contentsTitle}>
              {i18next.t('patients.cases.contents')}
            </div>
          </div>
          {CaseItemsList}
        </div>

        {(claims.permissions.deleteCases ||
          (claims.permissions.reopenCase && isReadOnly)) && (
          <div className={styles.deleteButtonContainer}>
            {claims.permissions.deleteCases && (
              <Button
                title={i18next.t('patients.cases.delete_button')}
                iconComponent={<TrashIcon />}
                color={ButtonColor.Danger}
                className={styles.deleteCaseButton}
                onClick={() => props.removeCase(caseId)}
                testId="delete_case_button"
              />
            )}
            {claims.permissions.reopenCase && isReadOnly && (
              <Button
                title={i18next.t('patients.cases.reopen_case_button')}
                color={ButtonColor.Primary}
                className={styles.deleteCaseButton}
                onClick={() => props.reopenCase(caseId)}
                testId="reopen_case_button"
              />
            )}
          </div>
        )}
      </div>
    );
  },
);
